import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ShoppingBag, Search, UserRound } from "lucide-react";
import { Logo } from "./Logo";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const NAV = [
  ["Latest", "/articles"],
  ["Research", "/articles?type=research"],
  ["Magazine", "/issues"],
  ["Read Online", "/reader/v14n4"],
  ["Store", "/shop"],
  ["Coaching", "/coaching"],
  ["Submit", "/submit"],
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { count, setOpen: openCart } = useCart();
  const { user } = useAuth();
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname, loc.search]);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header data-testid="site-header"
      className={`sticky top-0 z-40 transition-[background-color,box-shadow] duration-300 ${scrolled ? "bg-[#F7F8FA]/90 backdrop-blur-xl shadow-[0_1px_0_var(--line)]" : "bg-[var(--paper)]"}`}>
      <div className="hidden md:flex items-center justify-between px-6 lg:px-12 py-2 border-b rule eyebrow !text-[0.65rem]">
        <span>ISSN [placeholder] · Double-blind peer reviewed · Est. 2012</span>
        <span className="flex gap-6">
          <Link to="/contact" className="link-u" data-testid="topbar-contact-link">Contact</Link>
          <Link to="/admin" className="link-u" data-testid="topbar-admin-link">Editors</Link>
        </span>
      </div>
      <div className="flex items-center justify-between px-5 lg:px-12 h-[72px]">
        <Link to="/" data-testid="header-logo-link"><Logo /></Link>
        <nav className="hidden xl:flex items-center gap-7 text-[0.95rem] font-medium whitespace-nowrap">
          {NAV.map(([l, h]) => (
            <NavLink key={l} to={h} data-testid={`nav-${l.toLowerCase().replace(/\s/g, "-")}`} className="link-u">{l}</NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/articles" aria-label="Search" data-testid="header-search-btn" className="p-2 hover:text-[var(--red)] transition-colors"><Search size={19} /></Link>
          <Link to="/account" aria-label="Account" data-testid="header-account-btn" className="p-2 hover:text-[var(--red)] transition-colors flex items-center gap-2 text-sm font-medium">
            <UserRound size={19} /><span className="hidden xl:inline">{user ? (user.name || "Account").split(" ")[0] : "Sign in"}</span>
          </Link>
          <button onClick={() => openCart(true)} aria-label="Cart" data-testid="header-cart-btn" className="relative p-2 hover:text-[var(--red)] transition-colors">
            <ShoppingBag size={19} />
            {count > 0 && <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-[var(--red)] text-white text-[10px] w-4 h-4 grid place-items-center rounded-full">{count}</span>}
          </button>
          <Link to="/subscribe" data-testid="header-subscribe-btn" className="btn-red hidden sm:inline-flex px-5 py-2.5 text-sm font-semibold tracking-wide">Subscribe</Link>
          <button className="xl:hidden p-2" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-btn" aria-label="Menu">{open ? <X /> : <Menu />}</button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="xl:hidden overflow-hidden border-t rule bg-[var(--paper)]" data-testid="mobile-menu">
            <div className="px-5 py-6 flex flex-col gap-4">
              {[...NAV, ["Subscribe", "/subscribe"], [user ? "Account" : "Sign in", "/account"], ["Contact", "/contact"]].map(([l, h]) => (
                <Link key={l} to={h} className="f-serif text-3xl" data-testid={`mobile-nav-${l.toLowerCase().replace(/\s/g, "-")}`}>{l}</Link>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
